import React from "react";
import { useTranslation } from "react-i18next";

import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import { Box, Checkbox, Divider, Typography } from "@mui/material";

import { FullPageSpinner } from "@/components/Spinner";

import { PasswordMisuseDomainAlert } from "../service/domain";
import PasswordMisuseAlertRow from "./PasswordMisuseAlertRow";

type DomainDetailsProps = {
  isLoading?: boolean;
  domain?: PasswordMisuseDomainAlert | null;
  onAlertSelect?: (
    alertId: number,
    selected: boolean,
    alertIds?: number[],
  ) => void;
  selectedAlertIds?: number[];
};

const PasswordMisuseDomainDetails = ({
  isLoading = false,
  domain = null,
  onAlertSelect = () => {},
  selectedAlertIds = [],
}: DomainDetailsProps) => {
  const { t } = useTranslation();

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (domain) {
      if (checked) {
        const allAlertIds = domain.passwordMisuseAlerts.map(
          (alert) => alert.id,
        );
        onAlertSelect(-1, true, allAlertIds);
      } else {
        onAlertSelect(-1, false, []);
      }
    }
  };

  if (isLoading) {
    return <FullPageSpinner />;
  }

  if (domain === null) {
    return <NoDataAvailable />;
  }

  if (domain.passwordMisuseAlerts.length === 0) {
    return <AllCleared />;
  }

  let statusColor = "#808080";
  let statusIcon = undefined;
  let statusText = "";

  if (domain.remediation === "blocked") {
    statusColor = "#F44336";
    statusIcon = <BlockIcon sx={{ color: "#F44336", mr: 1 }} />;
    statusText = t("Browsing blocked");
  } else if (domain.remediation === "reuse") {
    statusColor = "#FFA500";
    statusIcon = <ReportProblemIcon sx={{ color: "#FFA500", mr: 1 }} />;
    statusText = t("Password reuse monitoring on");
  } else if (domain.remediation === "safe") {
    statusColor = "#4CAF50";
    statusIcon = <CheckCircleIcon sx={{ color: "#4CAF50", mr: 1 }} />;
    statusText = t("Password reuse monitoring off");
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        padding: ".5rem",
        height: "100%",
      }}
    >
      <DomainHeader
        domain={domain}
        statusMessage={statusText}
        statusColor={statusColor}
        statusIcon={statusIcon}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 2.6 }}>
          <Checkbox
            size="small"
            onChange={handleSelectAll}
            checked={
              domain.passwordMisuseAlerts.length > 0 &&
              domain.passwordMisuseAlerts.every((alert) =>
                selectedAlertIds.includes(alert.id),
              )
            }
            indeterminate={
              selectedAlertIds.length > 0 &&
              selectedAlertIds.length < domain.passwordMisuseAlerts.length
            }
            sx={{ mr: 1 }}
          />
          <Typography variant="subtitle1" fontWeight="bold">
            {t("URL")}
          </Typography>
        </Box>

        <Box sx={{ flex: 0.6, textAlign: "center" }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("Alert type")}
          </Typography>
        </Box>

        <Box sx={{ flex: 1.3, textAlign: "center" }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("User action")}
          </Typography>
        </Box>

        <Box sx={{ flex: 0.5, textAlign: "right" }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {t("Remediation")}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {domain.passwordMisuseAlerts.map((alert) => (
          <PasswordMisuseAlertRow
            key={alert.id}
            alert={alert}
            selected={selectedAlertIds.includes(alert.id)}
            onSelect={onAlertSelect}
          />
        ))}
      </Box>
    </Box>
  );
};

const DomainHeader = ({
  domain,
  statusMessage,
  statusColor,
  statusIcon,
}: {
  domain: PasswordMisuseDomainAlert;
  statusMessage: string;
  statusColor: string;
  statusIcon: React.ReactNode;
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {domain.name}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mt: 1,
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" sx={{ mr: 1 }}>
          {t("Last event")}: {domain.latestEvent.toLocaleString()}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: statusColor,
            ml: 2,
          }}
        >
          {statusIcon}
          <Typography variant="subtitle1" sx={{ color: statusColor }}>
            {statusMessage}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const NoDataAvailable = () => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Typography
        variant="h5"
        align="center"
        sx={{
          color: (theme) => theme.palette.text.secondary,
          maxWidth: "80%",
        }}
      >
        {t("Please select a domain to view the related alerts")}
      </Typography>
    </Box>
  );
};

const AllCleared = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Typography
        fontSize="20pt"
        gutterBottom
        fontWeight="bold"
        sx={{
          color: (theme) => theme.palette.text.secondary,
        }}
      >
        {t("No more alerts for that domain!")}
      </Typography>
    </Box>
  );
};

export default PasswordMisuseDomainDetails;
